'use client';

import {
  type ComponentProps,
  type KeyboardEvent,
  startTransition,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { Box, Combobox } from '@zillow/constellation-10';

// Constellation 10.14's `renderInputGroup` yields `groupProps` typed as
// `Partial<ComboboxInputGroupPropsInterface>`, but JSX-type inference for
// `<Combobox.InputGroup>` narrows to plain `RefAttributes` when a spread is
// involved. 10.17 fixes the inference. Cast to the component's own
// `ComponentProps` so the spread satisfies the narrow inferred type without
// `@ts-expect-error`.
type ComboboxInputGroupProps = ComponentProps<typeof Combobox.InputGroup>;
type ComboboxInputProps = ComponentProps<typeof Combobox.Input>;
import { MapPin } from 'lucide-react';
import { token } from '@/styled-system/tokens';
import { LucideIcon } from '@/src/components/LucideIcon';
import { AUTOCOMPLETE_MIN_PARTIAL_LENGTH, autocompleteV3 } from '@/src/lib/api/endpoints/area';
import type { AreaResponse, AutocompleteAreaType, AutocompleteResult, AutocompleteSuggestion } from '@/src/lib/api';
import { useRecentAreas } from '@/src/lib/hooks/area/useRecentAreas';
import { emitTrackSuggestedAreaEvent } from '@/src/analytics/hotpads/events';

const DEBOUNCE_MS = 200;
const CACHE_TTL_MS = 5 * 60 * 1000;
const NEAR_ME_VALUE = '__near_me__';
// Minimum typed characters before suggestions are fetched — and before Enter is
// allowed to search the top suggestion. Tracks the endpoint's own floor so the
// fetch, the options list, and the Enter handler can't drift from it.
const MIN_QUERY_LENGTH = AUTOCOMPLETE_MIN_PARTIAL_LENGTH;

// Token the ListingSearch wrapper and the active Rooms/Price triggers both
// paint, so the Location trigger reads as flush with the wrapper instead of
// literally transparent. See the `css` prop below for the full rationale.
const TRIGGER_BG_TOKEN = 'bg.action.neutral.alt.default';

const searchCache = new Map<string, { results: Array<AutocompleteSuggestion>; timestamp: number }>();

// Types displayed in ListingSearch: every area kind V3 surfaces (cities,
// neighborhoods, boroughs, zips, schools) plus listings (rental addresses).
const LISTING_SEARCH_TYPES = ['city', 'neighborhood', 'borough', 'zip', 'school', 'address'];

// Suggestions are already filtered and ranked server-side, so keep every option
// the API returned. Constellation's default filter keeps an option only if its
// label/meta substring-matches the typed text; a canonical address label
// ("555 Bryant St, San Francisco, CA") rarely contains exactly what the user
// typed (zip, "St" vs "Street", abbreviations), so the default would silently
// drop address results that the API did return.
const keepAllOptions = () => true;

type LocationComboboxProps = {
  /** Currently selected area (live mode) or seeded area (navigate mode). */
  area: AreaResponse | AutocompleteResult | null;
  /** IP-derived city for the "Near Me" affordance. */
  nearMeArea?: AreaResponse | null;
  /** Called when the user picks a suggestion or "Near Me". */
  onSelect: (result: AutocompleteResult) => void;
  /**
   * Called when Enter should run the search for a suggestion right away:
   * either the suggestion Constellation just selected on this keystroke, or
   * the top-ranked suggestion when nothing is highlighted. Fires after
   * `onSelect` for the same result.
   */
  onEnterSearch?: (result: AutocompleteResult) => void;
  /** Called on every keystroke so the parent can cancel recent-search seeding. */
  onUserInput?: () => void;
  /**
   * Called when the suggestions panel starts (or stops) showing at least one
   * option. Deliberately not raw open/close: an area that is already selected
   * puts its label in the input, and that label is long enough to look like a
   * query, so opening the panel on it yields an empty state with nothing to
   * react to.
   */
  onSuggestionsVisibleChange?: (visible: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  compact?: boolean;
  placeholder?: string;
};

type OptionShape = {
  value: string;
  label: string;
  meta?: string;
  groupHeading: string;
  result: AutocompleteResult;
};

function formatAreaLabel(area: AreaResponse | AutocompleteResult | null | undefined): string {
  if (!area) return '';
  // Zips identify by their code (the `name`), not "city, state".
  if (area.type?.toLowerCase() === 'zip') return area.name ?? '';
  if (area.city && area.state) return `${area.city}, ${area.state}`;
  return area.name ?? '';
}

/** Translation key for the dropdown group heading of an area suggestion. */
function areaTypeHeadingKey(
  areaType: AutocompleteAreaType | null | undefined,
): 'neighborhoods' | 'boroughs' | 'zipCodes' | 'schools' | 'cities' {
  switch (areaType) {
    case 'neighborhood':
      return 'neighborhoods';
    case 'borough':
      return 'boroughs';
    case 'zip':
      return 'zipCodes';
    case 'school':
      return 'schools';
    default:
      return 'cities';
  }
}

/**
 * Map a V3 suggestion onto the `AutocompleteResult` shape the rest of
 * ListingSearch (setArea, area display) still speaks. Listings navigate by
 * `lotIdEncoded`; areas by `resourceId`.
 */
function suggestionToResult(s: AutocompleteSuggestion): AutocompleteResult {
  return {
    type: s.type === 'listing' ? 'address' : (s.areaType ?? 'city'),
    name: s.label,
    resourceId: s.resourceId ?? undefined,
    lotIdEncoded: s.lotIdEncoded ?? undefined,
    city: s.city ?? undefined,
    county: s.county ?? undefined,
    state: s.state ?? undefined,
  };
}

function areaToResult(area: AreaResponse | null | undefined): AutocompleteResult | null {
  if (!area?.resourceId || !area.name) return null;
  return {
    type: 'City',
    name: area.name,
    resourceId: area.resourceId,
    city: area.city,
    state: area.state,
    county: area.county,
  };
}

/**
 * Internal Combobox wrapper for ListingSearch only. Wraps Constellation's
 * Combobox primitive with HotPads' debounced area autocomplete API, recent
 * areas, and the IP-derived Near Me affordance.
 */
export default function LocationCombobox({
  area,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  onSuggestionsVisibleChange,
  size = 'md',
  compact = false,
  placeholder,
}: LocationComboboxProps) {
  const tSearch = useTranslations('search');
  const { recentAreas } = useRecentAreas();
  const reactId = useId();
  const idPrefix = `loc-combobox-${reactId.replace(/:/g, '')}`;
  const areaLabel = formatAreaLabel(area);

  const [inputValue, setInputValue] = useState(() => {
    if (typeof document === 'undefined') return areaLabel;
    const serverInput = document.querySelector(`[data-hotpads-location-combobox="${idPrefix}"] input`);
    return serverInput instanceof HTMLInputElement && serverInput.value !== areaLabel ? serverInput.value : areaLabel;
  });
  const [results, setResults] = useState<Array<AutocompleteSuggestion>>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const preservedHydrationDraftRef = useRef(inputValue !== areaLabel);
  const onUserInputRef = useRef(onUserInput);

  useEffect(() => {
    onUserInputRef.current = onUserInput;
  }, [onUserInput]);

  // True while the input shows a label produced by a selection (or the
  // area-sync below) rather than text the user typed. Enter on committed text
  // belongs to the form's fallback submission (draftArea is already correct);
  // Enter on typed text must resolve through the suggestions instead.
  const inputCommittedRef = useRef(!preservedHydrationDraftRef.current);

  // Re-sync the visible label when the parent's `area` changes (e.g. live mode
  // after navigation lands and `useCurrentArea()` settles).
  useEffect(() => {
    if (preservedHydrationDraftRef.current) {
      preservedHydrationDraftRef.current = false;
      onUserInputRef.current?.();
      return;
    }

    setInputValue(areaLabel);
    inputCommittedRef.current = true;
  }, [areaLabel]);

  const nearMeResult = useMemo(() => areaToResult(nearMeArea), [nearMeArea]);

  const options = useMemo<Array<OptionShape>>(() => {
    const trimmed = inputValue.trim();
    const items: Array<OptionShape> = [];

    if (trimmed.length < MIN_QUERY_LENGTH) {
      if (nearMeResult) {
        items.push({
          value: NEAR_ME_VALUE,
          label: nearMeResult.city ?? nearMeResult.name ?? tSearch('nearMe'),
          meta: tSearch('nearMe'),
          groupHeading: tSearch('nearMe'),
          result: nearMeResult,
        });
      }
      for (const recent of recentAreas) {
        if (!recent.resourceId) continue;
        if (nearMeResult?.resourceId === recent.resourceId) continue;
        items.push({
          value: recent.resourceId,
          label: formatAreaLabel(recent) || recent.name || recent.resourceId,
          meta: [recent.county, recent.state].filter(Boolean).join(', ') || undefined,
          groupHeading: tSearch('recentlyViewed'),
          result: { ...recent, type: 'City' as const },
        });
      }
      return items;
    }

    for (const s of results) {
      const isListing = s.type === 'listing';
      const navValue = isListing ? s.lotIdEncoded : s.resourceId;
      if (!navValue) continue;
      // The option `value` must be a stable identifier that never coincides
      // with the typed text: Constellation marks an option `selected` when its
      // value equals the current input and then suppresses that option's
      // select event. A bare zip's resourceId IS the query string ("94110"),
      // so key off the suggestion's opaque `id` instead.
      const optionValue = s.id ?? navValue;
      items.push({
        value: optionValue,
        label: s.label,
        meta: [s.county, s.state].filter(Boolean).join(', ') || undefined,
        groupHeading: isListing ? tSearch('properties') : tSearch(areaTypeHeadingKey(s.areaType)),
        result: suggestionToResult(s),
      });
    }
    return items;
  }, [inputValue, nearMeResult, recentAreas, results, tSearch]);

  const [panelOpen, setPanelOpen] = useState(false);
  const suggestionsVisible = panelOpen && options.length > 0;
  const onSuggestionsVisibleChangeRef = useRef(onSuggestionsVisibleChange);

  useEffect(() => {
    onSuggestionsVisibleChangeRef.current = onSuggestionsVisibleChange;
  }, [onSuggestionsVisibleChange]);

  // Fires on the derived value, not inside the open handler: suggestions
  // arrive asynchronously after the debounced fetch, so the panel routinely
  // opens empty and fills in a beat later.
  useEffect(() => {
    onSuggestionsVisibleChangeRef.current?.(suggestionsVisible);
  }, [suggestionsVisible]);

  const handleInputValueChange = useCallback(
    (next: string) => {
      onUserInput?.();
      setInputValue(next);
      inputCommittedRef.current = false;
    },
    [onUserInput],
  );

  // Debounced autocomplete fetch once the user has typed MIN_QUERY_LENGTH chars.
  useEffect(() => {
    const trimmed = inputValue.trim();

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    // Urgent, unlike `setLoading(true)` below: this runs once per fetch rather
    // than once per keystroke, and deferring it would let a keydown land between
    // the results arriving and the commit, leaving the Enter handler reading the
    // previous query's options.
    const settle = (next: Array<AutocompleteSuggestion>) => {
      setResults(next);
      setLoading(false);
    };

    if (trimmed.length < MIN_QUERY_LENGTH) {
      settle([]);
      return;
    }

    // A committed label (selection or area sync) isn't a user query — skip the
    // fetch so selecting "Denver, CO" doesn't immediately re-search that label.
    // Keeps `results`, so re-opening the panel still shows them.
    if (inputCommittedRef.current) {
      setLoading(false);
      return;
    }

    startTransition(() => setLoading(true));

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const cacheKey = trimmed.toLowerCase();
        const cached = searchCache.get(cacheKey);
        const now = Date.now();
        if (cached && now - cached.timestamp < CACHE_TTL_MS) {
          settle(cached.results);
          return;
        }
        const response = await autocompleteV3(trimmed, { limit: 10, types: LISTING_SEARCH_TYPES });
        searchCache.set(cacheKey, { results: response, timestamp: now });
        settle(response);
      } catch {
        settle([]);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [inputValue]);

  // Holds the result Constellation selected during the current Enter
  // keystroke (its onOptionSelect fires synchronously inside the keydown
  // dispatch), so the bubble-phase handler below can search it immediately.
  // Cleared at capture phase so click-selections can't leak into a later
  // Enter press.
  const enterSelectionRef = useRef<AutocompleteResult | null>(null);

  // Selection side effects shared by Constellation-driven selections and the
  // Enter-on-typed-text path below.
  const selectOption = useCallback(
    (option: OptionShape) => {
      // Emitted inline, not deferred: the clickstream envelope is stamped when
      // `event()` runs (timestamp + page context), so delaying it past the
      // navigation would attribute this selection to the destination page.
      emitTrackSuggestedAreaEvent(
        size === 'lg'
          ? { triggerLocation: 'hp_homepage', triggerObject: 'hp_home_page_component|search' }
          : { triggerLocation: 'search_results', triggerObject: 'filter_panel' },
      );
      onSelect(option.result);
      setInputValue(option.label);
      inputCommittedRef.current = true;
    },
    [onSelect, size],
  );

  const handleOptionSelect = useCallback(
    (option: unknown) => {
      if (!option || typeof option !== 'object') return;
      const o = option as { value?: unknown };
      const value = typeof o.value === 'string' ? o.value : null;
      if (!value) return;
      const match = options.find((item) => item.value === value);
      if (!match) return;
      selectOption(match);
      enterSelectionRef.current = match.result;
    },
    [options, selectOption],
  );

  const handleEnterCapture = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    // An IME composition commit is not a search gesture: stop it before
    // Constellation's own Enter handling selects an option. (No preventDefault
    // — browsers don't implicit-submit composing Enters, and the IME still
    // needs its default handling.)
    if (event.nativeEvent.isComposing) {
      event.stopPropagation();
      return;
    }
    // Holding Enter auto-repeats keydowns; only the first press may search or
    // submit, so swallow repeats entirely (preventDefault also blocks the
    // form's implicit submission).
    if (event.repeat) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    enterSelectionRef.current = null;
  }, []);

  // Runs after Constellation's own input keydown handler (bubble phase).
  // Enter must run the search in the same keystroke (HP-8246):
  // - If Constellation selected a highlighted option during this keydown
  //   (it preventDefaults Enter whenever its dropdown is open — see
  //   use-combobox.js `onKeyDownInputEnter`), search that result.
  // - Otherwise, typed-but-unresolved text searches the top-ranked suggestion.
  //   This is decided off `inputCommittedRef`, NOT `event.defaultPrevented`,
  //   so it also covers keystrokes Constellation never handles: numpad Enter
  //   (its switch keys off event.code, and 'NumpadEnter' matches no case) and
  //   Enter after the dropdown already closed on a previous dead keystroke.
  // - Committed text (a selection's label) falls through to the wrapping
  //   form's implicit submission, which searches the already-staged draftArea.
  const handleEnterKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' || !onEnterSearch) return;
      const selected = enterSelectionRef.current;
      enterSelectionRef.current = null;
      if (selected) {
        onEnterSearch(selected);
        return;
      }
      if (inputCommittedRef.current || inputValue.trim().length < MIN_QUERY_LENGTH) return;
      // Unresolved typed text: the form fallback would search the wrong area
      // (near-me/current), so never let this keystroke reach it.
      event.preventDefault();
      const top = options[0];
      // Suggestions haven't arrived (debounce/network) or matched nothing —
      // swallow the keystroke rather than search an area the user didn't type.
      if (!top) return;
      selectOption(top);
      onEnterSearch(top.result);
    },
    [onEnterSearch, inputValue, options, selectOption],
  );

  return (
    // Keyboard interception wrapper only — focus and interactivity live on
    // the Combobox input inside.
    <Box
      asChild
      css={{
        // C11n 10.18 hardcodes clear-control spacing; compact SRP needs the Figma inset.
        '&[data-compact="true"] [data-c11n-component="Combobox.Clear"]': {
          marginInlineEnd: '-tighter',
          '& [data-c11n-component="IconButton"]': {
            marginInline: 'none',
          },
        },
      }}
    >
      <div
        data-hotpads-location-combobox={idPrefix}
        data-compact={compact ? 'true' : undefined}
        onKeyDownCapture={handleEnterCapture}
        onKeyDown={handleEnterKeyDown}
      >
        <Combobox
          data-c11n-component="listing-search-location"
          data-testid={idPrefix}
          appearance="input"
          freeForm
          showLabelForValue
          selectCallbackFrequency="always"
          fluidDropdown
          size={size}
          placeholder={placeholder ?? tSearch('findApartmentsIn')}
          aria-label={tSearch('searchByLocation')}
          loading={loading}
          value={inputValue}
          onChange={(next) => {
            if (typeof next === 'string') handleInputValueChange(next);
          }}
          onOptionSelect={handleOptionSelect}
          onOpenChange={setPanelOpen}
          options={options}
          optionFilter={keepAllOptions}
          // Forwarded to Combobox.Trigger — fills the flex-item parent in
          // ListingSearch so the location field grows to take the available width.
          // The data-c11n-component selector resets text-align on the underlying
          // <input> so the homepage hero's centered alignment doesn't cascade in.
          // NOTE: this can't be hoisted into one object with a conditional
          // spread for the Combobox.Input font-size override below -- Panda's
          // static extraction can't see through `...(size !== 'lg' && {...})`
          // and silently emits broken CSS (confirmed via `panda cssgen`). Two
          // fully static branches is the pattern the codebase already documents
          // as reliably extractable (see the container-query caveat in
          // docs/context/styling.md).
          css={
            size === 'lg'
              ? {
                  width: '100%',
                  borderRadius: 'pill',
                  boxShadow: 'none',
                  // The active Rooms/Price triggers (Button emphasis="outlined"
                  // tone="brand") paint `bg.action.neutral.alt.default`, the same
                  // token the ListingSearch wrapper itself uses -- so they read as
                  // flush with the wrapper rather than literally transparent. Match
                  // that token directly (not the `transparent` keyword) so this
                  // stays correct if the wrapper's token ever changes. This `css`
                  // prop lands on `Combobox.Trigger` itself, which paints
                  // `Combobox.Trigger.bg.default` (-> bg.neutral, blue.700 in dark
                  // mode) and repaints on hover/active/focus, so all of those states
                  // need overriding, not just the base. `Combobox.InputGroup` (a
                  // nested child) never had a background of its own.
                  backgroundColor: TRIGGER_BG_TOKEN,
                  _hover: { backgroundColor: TRIGGER_BG_TOKEN },
                  _active: { backgroundColor: TRIGGER_BG_TOKEN },
                  '&:has(input:focus)': { backgroundColor: TRIGGER_BG_TOKEN },
                  '& [data-c11n-component="Combobox.InputGroup"]': {
                    borderRadius: 'pill',
                  },
                  '& [data-c11n-component="Combobox.Input"]': {
                    textAlign: 'start',
                    fontWeight: 'bold',
                  },
                }
              : {
                  width: '100%',
                  paddingBlock: compact ? 'none' : undefined,
                  paddingInlineStart: 'none',
                  paddingInlineEnd: 'none',
                  borderRadius: 'pill',
                  boxShadow: 'none',
                  backgroundColor: TRIGGER_BG_TOKEN,
                  _hover: { backgroundColor: TRIGGER_BG_TOKEN },
                  _active: { backgroundColor: TRIGGER_BG_TOKEN },
                  '&:has(input:focus)': { backgroundColor: TRIGGER_BG_TOKEN },
                  '& [data-c11n-component="Combobox.InputGroup"]': {
                    borderRadius: 'pill',
                  },
                  '& [data-c11n-component="Combobox.Input"]': {
                    textAlign: 'start',
                    fontWeight: 'bold',
                  },
                }
          }
          renderInput={(inputProps) => (
            // AutocompleteV2Input mirrors legacy hotpads-web's testid on the same element, so the
            // shared Sonar monitors can assert one selector against both apps.
            <Combobox.Input
              {...(inputProps as ComboboxInputProps)}
              style={
                size === 'sm'
                  ? {
                      ...inputProps.style,
                      fontSize: token.var('fontSizes.body'),
                      fontWeight: token.var('fontWeights.regular'),
                      lineHeight: token.var('lineHeights.body'),
                      letterSpacing: token.var('letterSpacings.body'),
                    }
                  : inputProps.style
              }
              id={idPrefix}
              name="location"
              data-testid="AutocompleteV2Input"
            />
          )}
          renderInputGroup={(groupProps) => (
            <Combobox.InputGroup {...(groupProps as ComboboxInputGroupProps)}>
              <LucideIcon icon={MapPin} size={size === 'sm' ? 'sm' : 'md'} />
              {groupProps.children}
            </Combobox.InputGroup>
          )}
          renderOptionMedia={() => (
            <Combobox.OptionMedia>
              <LucideIcon icon={MapPin} size="sm" />
            </Combobox.OptionMedia>
          )}
        />
      </div>
    </Box>
  );
}
